import { BoxScanStep, DeliveryState, GetTourQuery, TourState } from 'data/__generated__';

import { getTimeFromDatetime } from 'helpers/dateTimes';

import { hasBoxReturnStarted, type Tour, type TourDelivery } from '../helpers';

/**
 * The detail query selects a SUPERSET of the list's `TourDeliveryFragment`, so every helper of the tours page
 * (`deliveryLabel`, `boxCounts`…) takes these types as they are — nothing to duplicate.
 */
export type DetailTour = GetTourQuery['tour'];
export type DetailDelivery = DetailTour['deliveries'][number];

/** Type of the photo the driver takes when closing a delivery — `DELIVERY_CLOSURE_PHOTO_TYPE` of the API's tour execution. */
export const DELIVERY_CLOSURE_PHOTO_TYPE = 'deliveryClosure';

/** The truck has left: from here on, a box with no `loadedAt` never went through the sas. */
export const hasTourDeparted = (tour: Pick<DetailTour, 'state'>): boolean =>
    [TourState.InDelivery, TourState.Finished].includes(tour.state);

/**
 * The truck is back: only now does a box with no `returnedAt` mean a MISSING box rather than a pending one.
 * Read off the boxes, not off the tour — the sas check is the warehouse's, done whenever the truck is home,
 * so the tour's own state says nothing about it.
 */
export const isTourBack = (tour: DetailTour): boolean => hasBoxReturnStarted(tour);

export type ScanStep = 'departureScannedAt' | 'loadedAt' | 'setupScannedAt' | 'returnedAt';

/**
 * Which scan steps a stop's boxes should have gone through by now. The steps follow each other: one stop of the tour
 * delivered, or the truck back, means every box left the sas — even on a tour whose state never moved past « Prête ».
 * The return does not imply the rangement: an undelivered stop comes back unshelved. `tour` is null for « à affecter ».
 */
export const dueScanSteps = (
    tour: Pick<Tour, 'state' | 'deliveries'> | null,
    delivery: Pick<TourDelivery, 'state'>,
): Record<ScanStep, boolean> => {
    const isDelivered = delivery.state === DeliveryState.Delivered;
    const isBack = tour != null && hasBoxReturnStarted(tour);
    const hasLeft =
        isDelivered ||
        isBack ||
        (tour != null &&
            (hasTourDeparted(tour) || tour.deliveries.some((stop) => stop.state === DeliveryState.Delivered)));

    return { departureScannedAt: hasLeft, loadedAt: hasLeft, setupScannedAt: isDelivered, returnedAt: isBack };
};

/** « HH:MM » of a timestamp, or a dash when the step has not happened. */
export const formatHour = (timestamp?: number | null): string =>
    timestamp ? (getTimeFromDatetime(new Date(timestamp)) ?? '—') : '—';

type ScannedBox = DetailDelivery['boxes'][number];

/** « HH:MM · Prénom Nom » of a scan step, or a dash when the box never went through it. */
const scanLabel = (box: ScannedBox, at: number | null | undefined, step: BoxScanStep): string => {
    if (!at) return '—';
    const author = box.scanHistory.find((entry) => entry.state === step)?.createdByName;
    return [getTimeFromDatetime(new Date(at)) ?? '—', author].filter(Boolean).join(' · ');
};

export const boxScans = (box: ScannedBox): { check: string; departure: string; setup: string; back: string } => ({
    check: scanLabel(box, box.departureScannedAt, BoxScanStep.DepartureScanned),
    departure: scanLabel(box, box.loadedAt, BoxScanStep.Loaded),
    setup: scanLabel(box, box.setupScannedAt, BoxScanStep.SetupScanned),
    back: scanLabel(box, box.returnedAt, BoxScanStep.Returned),
});
